import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Trophy, Crown, Medal, Award } from "lucide-react";
import SEO from "@/components/SEO";

// Podium rank metadata — gold, silver, bronze
const PODIUM = [
  { icon: Crown, ring: "border-orange-500", text: "text-orange-400", label: "1ST",  num: "text-orange-400" },
  { icon: Medal, ring: "border-zinc-300",   text: "text-zinc-200",   label: "2ND",  num: "text-zinc-200" },
  { icon: Award, ring: "border-amber-700",  text: "text-amber-500",  label: "3RD",  num: "text-amber-500" },
];

function PodiumRow({ runner, rank, challengeId }) {
  const meta = PODIUM[rank];
  const Icon = meta.icon;
  return (
    <div
      data-testid={`challenge-podium-${challengeId}-${rank + 1}`}
      className="flex items-center gap-3"
    >
      {runner.user_picture ? (
        <img
          src={runner.user_picture}
          alt={runner.user_name}
          className={`w-9 h-9 rounded-full object-cover border-2 ${meta.ring}`}
        />
      ) : (
        <div className={`w-9 h-9 rounded-full bg-zinc-800 border-2 ${meta.ring} grid place-items-center ${meta.text}`}>
          <Icon size={14} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className={`font-mono text-[9px] tracking-[0.3em] uppercase ${meta.text} flex items-center gap-1`}>
          <Icon size={10} /> {meta.label}
        </p>
        <p className="text-white font-display text-sm truncate leading-tight">
          {runner.user_name}
        </p>
      </div>
      <div className="text-right">
        <p className={`font-display text-lg leading-none ${meta.num}`}>{runner.total_km}</p>
        <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-zinc-500">
          KM · {runner.days}d
        </p>
      </div>
    </div>
  );
}

function ChallengeCard({ c }) {
  const isPast = c.status === "past";
  // Fallback to `winner` (legacy single object) when podium is missing.
  const podium = c.podium && c.podium.length > 0
    ? c.podium
    : (c.winner ? [c.winner] : []);
  return (
    <Link
      to={`/challenges/${c.challenge_id}`}
      data-testid={`challenge-card-${c.challenge_id}`}
      className={`hairline bg-zinc-900 overflow-hidden group hover:-translate-y-1 transition relative ${
        isPast ? "opacity-95" : ""
      }`}
    >
      {c.image && (
        <div className="aspect-[16/9] bg-zinc-800 overflow-hidden relative">
          <img
            src={c.image}
            alt=""
            className={`w-full h-full object-cover group-hover:scale-105 transition duration-500 ${
              isPast ? "grayscale-[35%]" : ""
            }`}
          />
          {isPast && (
            <span
              data-testid={`challenge-status-${c.challenge_id}`}
              className="absolute top-3 left-3 bg-black/70 backdrop-blur text-orange-400 font-mono text-[10px] tracking-[0.25em] uppercase px-2 py-1"
            >
              Concluded
            </span>
          )}
        </div>
      )}
      <div className="p-6">
        <div className="flex items-center gap-2 text-orange-500">
          <Trophy size={14} />
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase">
            {c.start_date} → {c.end_date}
          </p>
        </div>
        <h3 className="font-display text-2xl text-white mt-2 group-hover:text-orange-400 transition">
          {c.title}
        </h3>
        <p className="text-sm text-zinc-400 mt-2 line-clamp-3">{c.description}</p>
        <div className="mt-4 flex gap-4 text-xs font-mono text-zinc-400 uppercase tracking-wider">
          <span>{c.target_days} DAYS</span>
          {c.target_km_per_day && <span>{c.target_km_per_day} KM/DAY</span>}
        </div>

        {isPast && (
          <div
            data-testid={`challenge-winner-${c.challenge_id}`}
            className="mt-5 pt-4 border-t border-zinc-800"
          >
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-orange-500 mb-3 flex items-center gap-1.5">
              <Crown size={11} /> Podium
            </p>
            {podium.length > 0 ? (
              <div className="space-y-2.5">
                {podium.slice(0, 3).map((runner, idx) => (
                  <PodiumRow
                    key={runner.user_id}
                    runner={runner}
                    rank={idx}
                    challengeId={c.challenge_id}
                  />
                ))}
              </div>
            ) : (
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-500">
                No qualifying finishers
              </p>
            )}
          </div>
        )}
      </div>
    </Link>
  );
}

export default function Challenges() {
  const [challenges, setChallenges] = useState([]);

  useEffect(() => {
    api.get("/challenges").then((r) => setChallenges(r.data));
  }, []);

  const { active, past } = useMemo(() => {
    const a = [];
    const p = [];
    for (const c of challenges) {
      if (c.status === "past") p.push(c);
      else a.push(c);
    }
    return { active: a, past: p };
  }, [challenges]);

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12" data-testid="challenges-page">
      <SEO
        title="Running Challenges"
        path="/challenges"
        description="Take on OSCR running challenges — daily check-ins, photo proof, Strava links. Run virtually from any Odisha district. Earn Odia-soul badges like Konark Wheel and Mahanadi Mile."
      />
      <p className="font-mono text-[11px] tracking-[0.3em] text-orange-500 uppercase">PUSH FURTHER</p>
      <h1 className="font-display text-4xl sm:text-6xl text-white mt-2">Challenges</h1>
      <p className="text-zinc-400 mt-3 max-w-2xl leading-relaxed">
        Run further every month with OSCR&apos;s structured running challenges — designed for runners of
        every level across Odisha. Whether you&apos;re a beginner taking on your first 30-day streak or
        an experienced marathoner pushing 200 km in a month, our challenges keep you accountable
        and connected. Log your daily check-in with distance, notes, photo proof, or a Strava
        activity link. Climb the per-challenge leaderboard against runners from Bhubaneswar,
        Cuttack, Puri, Sambalpur, Berhampur and beyond. Earn Odia-soul badges — Konark Wheel for
        30-day streaks, Mahanadi Mile for 100 km, Puri Sunrise for dawn runs.
      </p>

      {/* Active / Upcoming */}
      <div className="mt-10" data-testid="challenges-active-section">
        <div className="flex items-baseline justify-between">
          <h2 className="font-display text-2xl text-white">Active & Upcoming</h2>
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-500">
            {active.length} live
          </span>
        </div>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          {active.length === 0 && (
            <div className="hairline bg-zinc-900 p-8 text-center text-zinc-500 font-mono text-sm md:col-span-2">
              No live challenges right now. Stay tuned.
            </div>
          )}
          {active.map((c) => (
            <ChallengeCard key={c.challenge_id} c={c} />
          ))}
        </div>
      </div>

      {/* Past Challenges */}
      {past.length > 0 && (
        <div className="mt-14" data-testid="challenges-past-section">
          <div className="flex items-baseline justify-between">
            <h2 className="font-display text-2xl text-white flex items-center gap-2">
              <Crown size={18} className="text-orange-500" />
              Past Challenges
            </h2>
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-500">
              {past.length} concluded
            </span>
          </div>
          <p className="text-xs font-mono tracking-wider text-zinc-500 mt-2 uppercase">
            Hall of fame — winners crowned by total km in window
          </p>
          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            {past.map((c) => (
              <ChallengeCard key={c.challenge_id} c={c} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
